
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  BookingAccessKind,
  DEFAULT_BOOKING_HORIZON_DAYS,
  equals as addressEquals,
  type BookingAccessInput,
} from '@shared/listing';
import {
  BookingWindowMode,
  useListingForm,
  type ListingFormMachine,
  type NewListingFormValues,
} from './use-new-listing-form.ts';
import {
  runUpdateListing,
  listingForEditKey,
  type EditableListing,
  type UpdateListingPatch,
} from './edit-listing-mutation.ts';
import { LISTINGS_QUERY_PREFIX } from '../Listings/listings-operations.ts';

export function fromListing(listing: EditableListing): NewListingFormValues {
  const address: NewListingFormValues['address'] = { formatted: listing.formattedAddress ?? '' };
  if (listing.addressLine1) address.line1 = listing.addressLine1;
  if (listing.addressLine2) address.line2 = listing.addressLine2;
  if (listing.city) address.city = listing.city;
  if (listing.region) address.region = listing.region;
  if (listing.postalCode) address.postalCode = listing.postalCode;
  if (listing.country) address.country = listing.country;
  if (listing.placeId) address.placeId = listing.placeId;
  if (typeof listing.lat === 'number') address.lat = listing.lat;
  if (typeof listing.lng === 'number') address.lng = listing.lng;
  if (listing.timezone) address.timezone = listing.timezone;

  const bookingAccess: BookingAccessInput =
    listing.bookingAccessKind === BookingAccessKind.SpecificAgents
      ? {
          kind: BookingAccessKind.SpecificAgents,
          userIds: [...(listing.bookingAccessUserIds ?? [])],
        }
      : { kind: BookingAccessKind.AllAgents };

  const bookingUntil = listing.bookingUntil ?? '';

  return {
    address,
    showingNotes: listing.showingNotes ?? '',
    bookingWindowMode: bookingUntil ? BookingWindowMode.Date : BookingWindowMode.Days,
    bookingHorizonDays: listing.bookingHorizonDays ?? DEFAULT_BOOKING_HORIZON_DAYS,
    bookingUntil,
    unavailability: [],
    bookingAccess,
    notifyUserIds: [...(listing.notifyUserIds ?? [])],
  };
}

export function toUpdatePatch(
  initial: NewListingFormValues,
  values: NewListingFormValues,
): UpdateListingPatch {
  const patch: UpdateListingPatch = {};

  if (!addressEquals(initial.address, values.address) && typeof values.address.timezone === 'string') {
    patch.address = {
      formatted: values.address.formatted.trim(),
      line1: values.address.line1 ?? null,
      line2: values.address.line2 ?? null,
      city: values.address.city ?? null,
      region: values.address.region ?? null,
      postalCode: values.address.postalCode ?? null,
      country: values.address.country ?? null,
      placeId: values.address.placeId ?? null,
      lat: values.address.lat ?? null,
      lng: values.address.lng ?? null,
      timezone: values.address.timezone,
    };
  }

  const oldNotes = initial.showingNotes.trim();
  const newNotes = values.showingNotes.trim();
  if (newNotes !== oldNotes) {
    if (newNotes === '') patch.clearShowingNotes = true;
    else patch.showingNotes = newNotes;
  }

  if (values.bookingWindowMode === BookingWindowMode.Date) {
    // Date mode: send the fixed date if it changed, or if we just switched into date mode.
    if (initial.bookingWindowMode !== BookingWindowMode.Date || values.bookingUntil !== initial.bookingUntil) {
      patch.bookingUntil = values.bookingUntil;
    }
  } else {
    // Days mode: switching away from a fixed date must clear it on the server and set a horizon.
    if (initial.bookingWindowMode === BookingWindowMode.Date) {
      patch.clearBookingUntil = true;
      patch.bookingHorizonDays = values.bookingHorizonDays;
    } else if (values.bookingHorizonDays !== initial.bookingHorizonDays) {
      patch.bookingHorizonDays = values.bookingHorizonDays;
    }
  }

  if (!sameAccess(initial.bookingAccess, values.bookingAccess)) {
    patch.bookingAccess =
      values.bookingAccess.kind === BookingAccessKind.SpecificAgents
        ? { kind: BookingAccessKind.SpecificAgents, userIds: [...values.bookingAccess.userIds] }
        : { kind: BookingAccessKind.AllAgents };
  }

  if (!sameIds(initial.notifyUserIds, values.notifyUserIds)) {
    patch.notifyUserIds = [...values.notifyUserIds];
  }

  return patch;
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function sameAccess(a: BookingAccessInput, b: BookingAccessInput): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === BookingAccessKind.SpecificAgents && b.kind === BookingAccessKind.SpecificAgents) {
    return sameIds(a.userIds, b.userIds);
  }
  return true;
}


export function useEditListingForm(
  listing: EditableListing,
  onSaved?: (id: string) => void,
): ListingFormMachine {
  const initialValues = useMemo(() => fromListing(listing), [listing]);
  const queryClient = useQueryClient();

  return useListingForm({
    initialValues,
    submit: async (values) => {
      const id = listing.id;
      if (!id) throw new Error('This listing is missing its id and can’t be edited.');
      const patch = toUpdatePatch(initialValues, values);
      if (Object.keys(patch).length > 0) {
        await runUpdateListing(id, patch);
        // Same staleness gap as create: the cached browse list (and this form's own
        // query on re-edit) would show pre-edit data for up to 30s.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: LISTINGS_QUERY_PREFIX }),
          queryClient.invalidateQueries({ queryKey: listingForEditKey(id) }),
        ]);
      }
      onSaved?.(id);
    },
  });
}
